import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest, ApiError } from '@/lib/api-client';
import { blOrderInvoicesKey } from '../api/use-bl-order-invoices';
import { allocatedDownpaymentKey } from '../api/use-allocated-downpayment';

const MIN_REASON_LENGTH = 5;

export interface ClearInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoiceId: string;
  readonly orderId: string;
  readonly blOrderId?: string;
  readonly invoiceLabel?: string;
  readonly onCleared: () => void;
}

function extractApiErrorMessage(err: Error, fallback: string): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body !== null) {
    const body = err.body as Record<string, unknown>;
    const msg = body['message'];
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg) && msg.length > 0 && typeof msg[0] === 'string') return msg[0];
  }
  return fallback;
}

function useClearMutation(args: {
  readonly invoiceId: string;
  readonly orderId: string;
  readonly blOrderId: string | undefined;
  readonly invoiceLabel: string;
  readonly onCleared: () => void;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: async (delete_reason) => {
      await apiRequest<undefined>(`/invoices/${encodeURIComponent(args.invoiceId)}`, {
        method: 'DELETE',
        body: { delete_reason },
      });
      return undefined;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order-advance-invoices', args.orderId] });
      void qc.invalidateQueries({ queryKey: ['order-invoices', args.orderId] });
      if (args.blOrderId !== undefined) {
        void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(args.blOrderId) });
        void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(args.blOrderId) });
        void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
      }
      toast.success(`${args.invoiceLabel} deleted.`);
      args.onCleared();
      args.onOpenChange(false);
    },
    onError: (err) => {
      toast.error(
        extractApiErrorMessage(err, `Failed to delete the ${args.invoiceLabel.toLowerCase()}.`),
      );
    },
  });
}

interface ClearFormProps {
  readonly reason: string;
  readonly setReason: (value: string) => void;
  readonly canSubmit: boolean;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

function ClearForm(props: ClearFormProps) {
  const { reason, setReason, canSubmit, isPending, onCancel, onSubmit } = props;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      className="space-y-3"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="delete_reason">Reason</Label>
        <Textarea
          id="delete_reason"
          data-testid="delete-invoice-reason"
          required
          minLength={MIN_REASON_LENGTH}
          maxLength={2000}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          disabled={isPending}
          rows={4}
        />
        <p className="text-muted-foreground text-xs">At least {MIN_REASON_LENGTH} characters.</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="destructive"
          data-testid="delete-invoice-confirm"
          disabled={!canSubmit}
        >
          {isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function ClearInvoiceDialog(props: ClearInvoiceDialogProps) {
  const { open, onOpenChange, invoiceId, orderId, blOrderId, onCleared } = props;
  const invoiceLabel = props.invoiceLabel ?? 'Downpayment invoice';
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const mutation = useClearMutation({
    invoiceId,
    orderId,
    blOrderId,
    invoiceLabel,
    onCleared,
    onOpenChange,
  });
  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= MIN_REASON_LENGTH && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete {invoiceLabel.toLowerCase()}</DialogTitle>
          <DialogDescription>
            This soft-deletes the invoice row. The row will no longer appear on the card. Provide a
            reason so the audit trail records why it was deleted.
          </DialogDescription>
        </DialogHeader>
        <ClearForm
          reason={reason}
          setReason={setReason}
          canSubmit={canSubmit}
          isPending={mutation.isPending}
          onCancel={() => {
            onOpenChange(false);
          }}
          onSubmit={() => {
            mutation.mutate(trimmed);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
